// Live preview of the marquee_select region, drawn as four corner
// BRACKETS (an L-shaped pair of arms at each corner) rather than a full
// rectangle. The highlighted cells (selection outline) are the precise
// feedback for what's selected; these brackets are just a light gestural
// guide showing the drag region. A full frame would imply a precision it
// can't deliver — it sits on the near laser-tip plane, so in stereo it
// has parallax against the model and wouldn't line up with the cells.
//
// Rendered as fat line SEGMENTS (LineSegments2) so the pixel width is
// honored (plain WebGL lines clamp to 1px). World-space, parented to the
// scene — matches the captured plane, which doesn't move during the drag.

import * as THREE from "three";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/addons/lines/LineSegmentsGeometry.js";
import type { Interaction } from "../interaction/types";

const RECT_COLOR = 0xa970d4; // violet — matches the selection laser
const RECT_WIDTH_PX = 5; // screen-space line width (LineSegments2 honors this)
// Each corner arm is this fraction of the shorter rect edge, so the
// brackets scale with the box and never overlap (0.3 + 0.3 < 1).
const BRACKET_FRACTION = 0.3;

export class MarqueeRect {
  private mesh: LineSegments2;

  constructor() {
    const geom = new LineSegmentsGeometry();
    // 8 segments × 6 floats — placeholder until the first update().
    geom.setPositions(new Array(8 * 6).fill(0));
    const mat = new LineMaterial({
      color: RECT_COLOR,
      linewidth: RECT_WIDTH_PX,
      transparent: true,
      opacity: 1.0,
      depthTest: false,
      depthWrite: false,
    });
    mat.resolution.set(window.innerWidth, window.innerHeight);
    this.mesh = new LineSegments2(geom, mat);
    this.mesh.renderOrder = 960;
    // World-space + dynamic positions → skip culling on a stale bounds.
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  get object3D(): THREE.Object3D {
    return this.mesh;
  }

  update(state: Extract<Interaction, { kind: "marquee_select" }>): void {
    const rMin = Math.min(state.startR, state.currentR);
    const rMax = Math.max(state.startR, state.currentR);
    const uMin = Math.min(state.startU, state.currentU);
    const uMax = Math.max(state.startU, state.currentU);

    const o = state.planeOrigin;
    const rd = state.planeRight;
    const ud = state.planeUp;
    const len = Math.min(rMax - rMin, uMax - uMin) * BRACKET_FRACTION;

    const pts: number[] = [];
    // One bracket segment from plane-local (r0,u0) to (r1,u1).
    const seg = (r0: number, u0: number, r1: number, u1: number): void => {
      pts.push(
        o.x + r0 * rd.x + u0 * ud.x,
        o.y + r0 * rd.y + u0 * ud.y,
        o.z + r0 * rd.z + u0 * ud.z,
        o.x + r1 * rd.x + u1 * ud.x,
        o.y + r1 * rd.y + u1 * ud.y,
        o.z + r1 * rd.z + u1 * ud.z,
      );
    };
    // Two arms at each corner, pointing along both adjacent edges.
    seg(rMin, uMin, rMin + len, uMin); // BL →
    seg(rMin, uMin, rMin, uMin + len); // BL ↑
    seg(rMax, uMin, rMax - len, uMin); // BR ←
    seg(rMax, uMin, rMax, uMin + len); // BR ↑
    seg(rMax, uMax, rMax - len, uMax); // TR ←
    seg(rMax, uMax, rMax, uMax - len); // TR ↓
    seg(rMin, uMax, rMin + len, uMax); // TL →
    seg(rMin, uMax, rMin, uMax - len); // TL ↓

    this.mesh.geometry.setPositions(pts);
    this.mesh.visible = true;
  }

  hide(): void {
    this.mesh.visible = false;
  }
}
